import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import {
  createAward,
  deleteAward,
  getAwards,
  updateAward,
} from "../../services/awardService";
import DateInput from "../../components/Inputs/DateInput";
import { awardSchema } from "../../validators/awardValidators";
import { format } from "date-fns";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Award = () => {
  const [awards, setAwards] = useState([]);
  const [filteredAwards, setFilteredAwards] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [awardToEdit, setAwardToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [awardToDelete, setAwardToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadAwards();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = awards;
    if (searchTerm.trim() !== "") {
      filtered = awards.filter((item) =>
        `${item?.name} ${item?.institution.name} ${item?.ranking}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredAwards(filtered);
  }, [searchTerm, awards, selectedInstitution]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadAwards = async () => {
    setLoading(true);
    try {
      const { data } = await getAwards();
      setAwards(data.data);
      setFilteredAwards(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load awards");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (awardToEdit) {
      onUpdateAward(payload, resetForm);
    } else {
      onCreateAward(payload, resetForm);
    }
  };

  const onCreateAward = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createAward(payload);

      setAwards((prev) => [...prev, data.data]);

      toast.success("Award added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response.data.error || "Award was not added, try again!");
      }
    }
  };

  const handleEdit = (category) => {
    setAwardToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateAward = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalAwards = [...awards];

      const awardIndex = originalAwards.findIndex(
        (award) => award.id === awardToEdit.id
      );

      if (awardIndex === -1) throw new Error("Awards not found.");

      const { data } = await updateAward(awardToEdit.id, payload);

      originalAwards[awardIndex] = data.data;

      setAwards(originalAwards);

      setAwardToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Award updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Award not updated, try again!");
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setAwardToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteAward = async () => {
    const originalAwards = [...awards];

    const newAwards = originalAwards.filter(
      (award) => award.id !== awardToDelete.id
    );

    setAwards(newAwards);

    try {
      await deleteAward(awardToDelete?.id);

      toast.success("Award deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Award not deleted, try again later"
        }`
      );

      setAwards(originalAwards);
    } finally {
      setIsDeleteModalOpen(false);

      setAwardToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setAwardToEdit(null);
    setAwardToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Award",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Date Awarded",
      dataIndex: "date",
      key: "date",
      render: (dateStr) => {
        if (!dateStr) return "N/A";

        try {
          return format(new Date(dateStr), "do MMMM yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Ranking",
      dataIndex: "ranking",
      key: "ranking",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Accolade",
      dataIndex: "accolade",
      key: "accolade",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Award">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Award">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Awards" subtitle1="Awards" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Awards"
            count={filteredAwards.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Award"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredAwards.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              !isInstAdmin && (
                <div className="mb-2 py-2 flex gap-2">
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {awardToEdit ? "Edit Award" : "Add Award"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: awardToEdit ? awardToEdit?.name : "",
                ranking: awardToEdit ? awardToEdit?.ranking : "",
                accolade: awardToEdit ? awardToEdit?.accolade : "",
                date: awardToEdit ? awardToEdit?.date : "",
                institution_id: awardToEdit
                  ? awardToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={awardSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          awardToEdit
                            ? awardToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <TextInput
                      name="name"
                      title="Award name"
                      placeholder="e.g. Best Research Paper Award"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextInput
                      name="ranking"
                      title="Ranking"
                      placeholder="e.g. 1st Place, Top 5"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextInput
                      name="accolade"
                      title="Accolade"
                      placeholder="e.g. Excellence in Sustainable Engineering"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <DateInput
                      name="date"
                      title="Date Awarded"
                      placeholder="Select award date"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteAward}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Award;
